import type { AuditEvent } from "@apple-bee/contracts";
import { useQuery } from "@tanstack/react-query";
import { Activity, ShieldCheck } from "lucide-react";
import { api } from "../api";
import { EmptyState, LoadingBlock } from "../components/Feedback";
import { dateTimeFormatter } from "../format";

const actionLabels: Record<string, string> = {
  LEGACY_DATA_IMPORTED: "Импортированы данные прототипа",
  LEGACY_REFERENCE_APPROVED: "Запись прототипа одобрена",
  LEGACY_REFERENCE_REJECTED: "Запись прототипа отклонена",
  AUTH_LOGIN_SUCCEEDED: "Успешный вход",
  AUTH_LOGIN_FAILED: "Неудачная попытка входа",
  AUTH_LOGOUT: "Выход из системы",
  AUTH_RECOVERY_CODE_USED: "Использован резервный код",
  ACCOUNT_CREATED_FROM_INVITATION: "Аккаунт создан",
  USER_INVITATION_CREATED: "Создано приглашение",
  USER_INVITATION_REVOKED: "Приглашение отозвано",
  USER_ENABLED: "Пользователь включён",
  USER_DISABLED: "Пользователь отключён",
  USER_MFA_RESET: "Сброшена 2FA",
  USER_MFA_RESET_BREAK_GLASS: "Аварийно сброшена 2FA",
  USER_PASSWORD_RESET_REQUESTED: "Создана ссылка восстановления пароля",
  USER_PASSWORD_RESET_COMPLETED: "Пароль восстановлен",
  STOCK_MOVEMENT_CREATED: "Складское движение",
  STOCK_MOVEMENT_REVERSED: "Сторно движения",
  SHIPMENT_REQUEST_CREATED: "Создана заявка",
  SHIPMENT_REQUEST_APPROVED: "Заявка одобрена",
  SHIPMENT_REQUEST_REJECTED: "Заявка отклонена",
  SHIPMENT_SHIPPED: "Отгрузка проведена",
  SHIPMENT_ACCEPTED: "Отгрузка принята",
  ACCOUNTING_EXPORT_CREATED: "Создан бухгалтерский файл",
  ACCOUNTING_EXPORT_CONFIRMED_IN_1C: "Подтверждено отражение в 1С",
  GOOGLE_SHEETS_STOCK_SUMMARY_SENT: "Сводка отправлена в Google Sheets",
};

export function AuditPage() {
  const query = useQuery({
    queryKey: ["audit"],
    queryFn: () => api<{ events: AuditEvent[] }>("/api/audit?limit=100"),
    refetchInterval: 60_000,
  });

  if (query.isLoading) {
    return <LoadingBlock label="Загружаем журнал аудита…" />;
  }

  return (
    <div className="page-stack">
      <section className="page-heading">
        <div>
          <p className="eyebrow">Контроль действий</p>
          <h2>Журнал аудита</h2>
          <p>
            Записи добавляются сервером и защищены от изменения или удаления
            через приложение.
          </p>
        </div>
        <span className="audit-seal">
          <ShieldCheck size={20} />
          Только добавление
        </span>
      </section>

      <section className="panel">
        {query.data?.events.length ? (
          <div className="audit-list">
            {query.data.events.map((event) => (
              <article className="audit-row" key={event.id}>
                <span className="audit-row__icon">
                  <Activity size={17} />
                </span>
                <div className="audit-row__main">
                  <strong>{actionLabels[event.action] ?? event.action}</strong>
                  <span>
                    {event.actorName ?? "Система"}
                    {event.actorEmail ? ` · ${event.actorEmail}` : ""}
                  </span>
                </div>
                <div className="audit-row__entity">
                  <span>{event.entityType}</span>
                  {event.entityId ? <code>{event.entityId.slice(0, 8)}</code> : null}
                </div>
                <div className="audit-row__time">
                  <strong>{dateTimeFormatter.format(new Date(event.occurredAt))}</strong>
                  <span>{event.ipAddress ?? "IP не записан"}</span>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <EmptyState
            title="Событий ещё нет"
            description="Первое действие пользователя появится здесь автоматически."
          />
        )}
      </section>
    </div>
  );
}
